import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import routes from '../../../routers'
import { NextArrow, NextPrev } from '../../../components/SvgIcon'
import ModalAccount from '../../../components/Modals/ModalAccount'
import LoadingSpinner from '../../../components/LoadingSpinner'
import Layout from '../../../components/Layout'
import { FormProblems } from '../../../components/FieldFeedback'
import StepsComponent from './StepsComponent'
import RecipeTitle from './RecipeTitle'

import '../style.css'

// Typing boxes in the form, in page order, that Enter can move between
const typingBoxes = (container) =>
  [
    ...container.querySelectorAll(
      'input:not([type=checkbox]):not([type=radio]):not([type=file]):not([type=hidden]), textarea'
    ),
  ].filter((box) => !box.disabled && box.offsetParent !== null)

const focusBoxAfter = (form, box) => {
  const boxes = typingBoxes(form)
  const next = boxes[boxes.indexOf(box) + 1]
  if (next) next.focus()
}

const LayoutRecipe = ({
  onSubmit,
  children,
  isLoading,
  setRequired,
  navigationPrev,
  navigationNext,
  step = 1,
  disableButtonSave,
  navigationPrevTitle = 'Next step',
  title = '',
  subTitle = '',
  // Problems to fix that aren't next to one field, shown above the buttons
  problems = [],
  // The "saved to drafts" note shows once the draft really is saved, not on the click
  draftSaved = false,
  onDraftSavedClose = () => {},
  // Called when Enter is pressed in the last box of a [data-enter-adds-row] list,
  // for example the last ingredient, so typing straight through adds a new row
  onEnterInLastRow,
}) => {
  const navigation = useNavigate()

  // Enter in a one-line box moves to the next box instead of submitting the form.
  // Submitting on Enter saved half-typed recipes or jumped to another page.
  const onKeyDown = (e) => {
    const box = e.target
    if (e.key !== 'Enter' || e.defaultPrevented || e.nativeEvent.isComposing) return
    if (box.tagName !== 'INPUT' || ['checkbox', 'radio', 'file'].includes(box.type)) return
    e.preventDefault()
    const form = e.currentTarget
    const rows = onEnterInLastRow && box.closest('[data-enter-adds-row]')
    if (rows) {
      const rowBoxes = typingBoxes(rows)
      if (rowBoxes[rowBoxes.length - 1] === box) {
        onEnterInLastRow()
        // Wait for the new row to appear, then put the cursor in it
        return requestAnimationFrame(() => focusBoxAfter(form, box))
      }
    }
    focusBoxAfter(form, box)
  }

  return (
    <Layout title={`Add recipe step-${step}`}>
      <div className="innerFormManageRecipe">
        <form onSubmit={onSubmit} onKeyDown={onKeyDown} className="containerRecipeMain recipeForm">
          <StepsComponent step={step} />
          <RecipeTitle title={title} subTitle={subTitle} />
          <div className="recipeContent">
            <FormProblems problems={problems} />
            {children}
          </div>
          <div className="recipeActionBar">
            <div className="recipeActionBarInner">
              <div className="recipeActionBarLeft">
                {navigationPrev ? (
                  <Link to={navigationPrev} className="recipeBack">
                    <NextPrev /> Back
                  </Link>
                ) : null}
                <button
                  type="submit"
                  className="recipeSave"
                  disabled={disableButtonSave}
                  onClick={() => setRequired(false)}
                >
                  Save to drafts
                </button>
              </div>
              {navigationNext ? (
                <button
                  type="submit"
                  disabled={disableButtonSave}
                  onClick={() => setRequired(true)}
                  className="recipeNextButton"
                >
                  {navigationPrevTitle}
                  <NextArrow />
                </button>
              ) : null}
            </div>
          </div>
        </form>
        <ModalAccount show={draftSaved} onHide={onDraftSavedClose}>
          <div className="innerModalRemoveUser">
            <p className="textModalRemoveFirst">
              Success! Your recipe has been saved to the Drafts folder.
            </p>
            <button onClick={onDraftSavedClose} className="removeUserButton modalButtonSuccess">
              Continue Editing
            </button>
            <button
              onClick={() => navigation(routes.recipeDraft)}
              className="removeUserButton modalButtonSuccess viewRecipeBtn"
            >
              View Recipe Drafts
            </button>
          </div>
        </ModalAccount>
        {isLoading && <LoadingSpinner />}
      </div>
    </Layout>
  )
}

export default LayoutRecipe
